import { saveAs } from 'file-saver';
import { toast } from '../../../common/toast';
import logger from '../../../utils/logger';
import { mergeAndDownload } from '../Workups/WorkupDownload.js';
import { generateEvidenceFileName } from '../SummaryCaseTable/utils/helpers.js';
import {
  EVIDENCE_PARTIES,
  filterNyscefFilesByParty,
  getNyscefRawFilename,
  uniqueNyscefDownloadFilename,
} from './nyscefExhibitUtils.js';

const partyDisplayLabel = (party) =>
  party === EVIDENCE_PARTIES.TOWN ? 'Town' : 'Aventine';

const fetchPdfBytes = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

const resolveCasesInScope = ({ negotiationObj, selectedRows, rows }) => {
  const rowsToUse = selectedRows?.length ? selectedRows : rows;
  const parcelIds = new Set(rowsToUse?.map((r) => r.parcel_id).filter(Boolean) ?? []);
  return (negotiationObj?.cases ?? []).filter((c) => parcelIds.has(c.parcel_id));
};

const collectEvidenceEntries = (cases, party) => {
  const entries = [];
  for (const caseItem of cases) {
    const files = filterNyscefFilesByParty(caseItem?.NyscefS3Files, party);
    for (const file of files) {
      entries.push({
        parcelId: caseItem.parcel_id,
        address: caseItem.Address,
        file,
        filename: getNyscefRawFilename(file),
      });
    }
  }
  return entries;
};

const buildDownloadFileName = (negotiationObj) => generateEvidenceFileName(
  negotiationObj?.MuniCode || negotiationObj?.muniCode,
  negotiationObj?.TaxYear || negotiationObj?.taxYear,
  negotiationObj?.CourtDate,
);

const sortEvidenceEntries = (entries) =>
  [...entries].sort((a, b) => {
    const addressCmp = String(a.address || a.parcelId).localeCompare(String(b.address || b.parcelId));
    if (addressCmp !== 0) return addressCmp;
    return String(a.filename).localeCompare(String(b.filename));
  });

/**
 * Bulk-download NYSCEF exhibit PDFs for cases in scope, filtered by party (town or aventine).
 */
export const downloadBulkEvidence = async ({
  negotiationObj,
  selectedRows = [],
  rows = [],
  party,
  mergePdfs = false,
  onLoadingChange = null,
} = {}) => {
  const normalizedParty = String(party || '').toLowerCase();
  if (!Object.values(EVIDENCE_PARTIES).includes(normalizedParty)) {
    toast.error('Select town or our evidence to download.');
    return { success: false };
  }

  const casesInScope = resolveCasesInScope({ negotiationObj, selectedRows, rows });
  if (!casesInScope.length) {
    toast.error('No cases in scope for download.');
    return { success: false };
  }

  const entries = sortEvidenceEntries(collectEvidenceEntries(casesInScope, normalizedParty));
  if (!entries.length) {
    toast.error(`No ${partyDisplayLabel(normalizedParty).toLowerCase()} evidence files found for the selected cases.`);
    return { success: false };
  }

  onLoadingChange?.(true);

  try {
    const downloadName = buildDownloadFileName(negotiationObj);

    if (mergePdfs) {
      const urls = [];
      const failed = [];
      for (const entry of entries) {
        if (entry.file?.Url) {
          urls.push(entry.file.Url);
        } else {
          failed.push(entry);
        }
      }

      if (!urls.length) {
        toast.error('No evidence PDFs could be fetched for download.');
        return { success: false };
      }

      await mergeAndDownload(urls, { fileName: downloadName });
      const added = urls.length;
      if (failed.length > 0) {
        toast.warning(`Merged ${added} of ${entries.length} evidence files. ${failed.length} failed to fetch.`);
      } else {
        toast.success(`Downloaded merged evidence PDF (${added} file${added === 1 ? '' : 's'})`);
      }
      return { success: true, added, failed: failed.length };
    }

    const jszipModule = await import('jszip');
    const JSZip = jszipModule.default ?? jszipModule;
    const zip = new JSZip();
    const failed = [];
    const usedPaths = new Set();

    for (const entry of entries) {
      try {
        const pdfBytes = await fetchPdfBytes(entry.file.Url);
        const zipPath = uniqueNyscefDownloadFilename(entry.filename || entry.parcelId, usedPaths);
        zip.file(zipPath, pdfBytes);
      } catch (error) {
        failed.push({ parcelId: entry.parcelId, filename: entry.filename });
        logger.error(`Error fetching evidence for ${entry.parcelId}:`, error);
      }
    }

    const added = entries.length - failed.length;
    if (added === 0) {
      toast.error('No evidence PDFs could be fetched for download.');
      return { success: false };
    }

    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 3 },
    });

    saveAs(zipBlob, `${downloadName}.zip`);

    if (failed.length > 0) {
      toast.warning(`Downloaded ${added} of ${entries.length} evidence files. ${failed.length} failed to fetch.`);
    } else {
      toast.success(`Downloaded ${added} evidence file${added === 1 ? '' : 's'}`);
    }

    return { success: true, added, failed: failed.length };
  } catch (error) {
    logger.error('Error downloading bulk evidence:', error);
    toast.error(error?.message || 'Failed to download evidence.');
    return { success: false, error };
  } finally {
    onLoadingChange?.(false);
  }
};
